import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ProgressToolCall, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {ToolIconDisplay} from 'llm/chat-library/tool-details/ToolIconDisplay';
import {ToolType} from 'llm/chat-library/tool-details/types';
import {useToolDisplayName} from 'llm/chat-library/tool-details/useToolDisplayName';
import {useToolIcon} from 'llm/chat-library/tool-details/useToolIcon';
import {getWebCitationUrlParts} from 'llm/markdown/LLMMarkdownUtils';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import EntryChip from 'ui-library/chips/EntryChip';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';

export interface CitationSourceChipProps {
  citationId: string;
  citationNumber: number;
  onCitationClick: (toolId: string, isSourceCitation?: boolean) => void;
  toolCall: ProgressToolCall | undefined;
}

export default function CitationSourceChip({
  citationId,
  citationNumber,
  onCitationClick,
  toolCall,
}: CitationSourceChipProps): JSX.Element {
  const getToolDisplayNameFormatted = useToolDisplayName();
  const getToolIconResolved = useToolIcon();

  const handleClick = React.useCallback((): void => {
    onCitationClick(citationId, true);
  }, [onCitationClick, citationId]);

  const toolDescription = toolCall
    ? getToolDisplayNameFormatted(toolCall.tool, toolCall.args, ToolCallStatus.COMPLETED)
    : 'View citation details';

  const tooltipContent = (
    <div>
      <div>Activity: {toolDescription}</div>
      <div>Citation #{citationNumber}</div>
    </div>
  );

  // For ReadBenchlingDocumentTool, extract document_id from args to render EntryChip
  const documentId =
    toolCall?.tool === ToolType.ReadBenchlingDocumentTool
      ? (toolCall.args?.document_id as string | undefined)
      : undefined;

  const toolIcon = toolCall ? getToolIconResolved(toolCall.tool) : null;

  const chipText =
    toolIcon != null ? (
      <span className={css(styles.chipTextWithIcon)}>
        <ToolIconDisplay
          icon={toolIcon}
          glyphClassName={css(styles.chipIconGlyph)}
          imgClassName={css(styles.chipIconImg)}
        />
        {toolCall ? toolDescription : ''}
      </span>
    ) : toolCall ? (
      toolDescription
    ) : (
      ''
    );

  return (
    <li className={css(styles.container)} value={citationNumber}>
      <Tooltip text={tooltipContent} placement={FloatingPlacement.TOP}>
        {documentId ? (
          <EntryChip id={documentId} size={ChipSize.SMALL} onClick={handleClick} />
        ) : (
          <Chip size={ChipSize.SMALL} text={chipText} onClick={handleClick} />
        )}
      </Tooltip>
    </li>
  );
}

export interface WebCitationSourceProps {
  url: string;
}

export function WebCitationSource({url}: WebCitationSourceProps): JSX.Element | null {
  const urlParts = getWebCitationUrlParts(url);
  const cleanUrl = urlParts?.href ?? url;

  const handleClick = React.useCallback((): void => {
    window.open(cleanUrl, '_blank', 'noopener,noreferrer');
  }, [cleanUrl]);

  if (urlParts == null) {
    return null;
  }

  return (
    <li className={css(styles.webCitationItem)}>
      <Tooltip text={cleanUrl} placement={FloatingPlacement.TOP}>
        <Chip size={ChipSize.SMALL} text={urlParts.domain} onClick={handleClick} icon="globe" />
      </Tooltip>
    </li>
  );
}

const styles = StyleSheet.create({
  container: {
    listStylePosition: 'inside',
  },
  citationNumber: {
    fontSize: '12px',
    fontWeight: 'bold',
  },
  chipTextWithIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  },
  chipIconGlyph: {
    fontSize: 12,
    flexShrink: 0,
  },
  chipIconImg: {
    width: 12,
    height: 12,
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  webCitationItem: {
    listStyle: 'none',
  },
});
